/**
 * `<caos-carousel>` — a rotating gallery of images, moved by hand or on a timer.
 * (Catalog: Data display › Carousel — a run of pictures shown one at a time, moved by hand or on a timer, that holds still for a reader who asked for less motion.)
 *
 * CAOS-609. A rotator leading a landing page is the ordinary case on a public surface, and the
 * palette had nothing for it: the card grid lays a set out side by side and the split view holds two
 * panes, but neither shows one picture at a time and moves between them.
 *
 * IMAGES ARE A SHAPE, NOT A COMPONENT. The `images` property is the list —
 * `{ src, alt, heading?, description?, href? }[]` — and an image is nothing more than an entry in
 * it, the same fold the accordion applies to a section and the menu to an item. There is no
 * `caos-carousel-image` to place: a page author never places an image, they configure a carousel.
 * An entry with no `src` is dropped rather than drawn as an empty slide.
 *
 * AUTOPLAY IS DECLARED, NEVER ASSUMED. `autoplay` is off unless asked for, `interval` says how long
 * each picture holds (5000ms by default), and pausing while a reader is engaged is what happens
 * unless `no-pause-on-hover` says otherwise. The timer runs only while the element is
 * connected, there is more than one picture, and the interval is a positive number; it is cleared on
 * disconnect. That is the shape `toast.ts` already uses for its auto-dismiss, including the part
 * most easily forgotten: focus pauses it as well as the pointer, or somebody moving through it by
 * keyboard is left working against a moving target.
 *
 * LESS MOTION IS HONOURED IN BEHAVIOUR, NOT ONLY IN PAINT. A stylesheet can drop a transition but it
 * cannot stop a timer, so the preference is READ — `prefers-reduced-motion: reduce`, through
 * `matchMedia` on connection and again whenever it changes, guarded the way `split-view.ts` guards
 * its own query. While it holds, autoplay does not start, `play()` is refused, and the gallery lands
 * on the first picture. Moving by hand still works: the preference is about motion nobody asked for,
 * not about navigation.
 *
 * A11y: the gallery is a `role="region"` announced as a carousel; each slide is a `role="group"`
 * announced as a slide and labelled with its position; the slides not showing are `hidden`, so they
 * are out of the tab order and the accessibility tree rather than merely out of sight. Previous and
 * next are real buttons, each dot is a button naming the picture it goes to, Arrow Left/Right move
 * and Home/End go to the first and last. A polite status region reports the new position on a move
 * somebody made, and is silenced while autoplay is running so a reader is not interrupted every few
 * seconds by motion they did not ask for. Styling seams: `::part(carousel)`, `::part(viewport)`,
 * `::part(slide)`, `::part(image)`, `::part(caption)`, `::part(heading)`, `::part(description)`,
 * `::part(controls)`, `::part(previous)`, `::part(next)`, `::part(dots)`, `::part(dot)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

/** One picture in a carousel — a shape inside the list, never a placed component of its own. */
export interface CaosCarouselImage {
  /** The address of the picture. An entry without one is dropped. */
  src: string;
  /** What the picture shows, for somebody who cannot see it. Empty marks a picture carrying no meaning. */
  alt: string;
  /** A line of display text drawn over the picture. */
  heading?: string;
  /** A sentence under the heading. */
  description?: string;
  /** Where the slide goes when it is followed, which makes the whole slide a link. */
  href?: string;
}

/** How long a picture holds before autoplay moves on, when the author names no interval. */
const DEFAULT_INTERVAL = 5000;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

let uid = 0;

const sheet = makeSheet(`
  :host { display: block; }

  .carousel {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-2);
    font-family: var(--caos-font);
  }

  .viewport {
    position: relative;
    overflow: hidden;
    aspect-ratio: 16 / 9;
    border-radius: var(--caos-radius);
    background: var(--caos-color-surface-2);
  }

  .slide {
    position: absolute;
    inset: 0;
    transition: opacity var(--caos-duration-base) var(--caos-ease-standard);
  }

  .link {
    display: block;
    width: 100%;
    height: 100%;
    color: inherit;
    text-decoration: none;
  }
  a.link:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }

  .image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .caption {
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-1);
    padding: var(--caos-space-3);
    background: var(--caos-color-surface);
  }

  .heading {
    font-weight: 600;
    line-height: 1.3;
    color: var(--caos-color-text);
  }

  .description {
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
  }

  .controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--caos-space-2);
  }

  .step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--caos-space-1);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    cursor: pointer;
  }
  .step:hover { background: var(--caos-state-hover-bg); }
  .step:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  /* One glyph, turned. There is a single chevron in the icon set and no directional pair. */
  .step--previous .glyph { transform: rotate(90deg); }
  .step--next .glyph { transform: rotate(-90deg); }

  .dots {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
  }

  .dot {
    width: 8px;
    height: 8px;
    padding: 0;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-surface);
    cursor: pointer;
  }
  .dot[aria-current="true"] { background: var(--caos-color-accent); }
  .dot:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  /* The position, for a screen reader only. Same shape the file field hides its native input with. */
  .status {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  /* Less motion: nothing eases. The timer is stopped in script — a stylesheet cannot do that. */
  @media (prefers-reduced-motion: reduce) {
    .slide { transition: none; }
  }
`);

/** Only entries that name a picture; an entry without one has nothing to show. */
function usable(list: readonly CaosCarouselImage[]): CaosCarouselImage[] {
  return list.filter((image) => typeof image?.src === 'string' && image.src.trim() !== '');
}

export class CaosCarousel extends CaosElement {
  static get observedAttributes(): string[] {
    return ['autoplay', 'interval', 'no-pause-on-hover', 'label'];
  }

  private _images: CaosCarouselImage[] = [];
  private _index = 0;
  /** Whether autoplay has been asked for — by the attribute, or by `play()`. */
  private playRequested = false;
  private timer: number | null = null;
  /** The reader's system asks for reduced motion, so nothing moves unless they move it. */
  private reducedMotion = false;
  private mql: MediaQueryList | null = null;
  private hovered = false;
  private focused = false;
  private readonly idBase = `caos-carousel-${(uid += 1)}`;
  private readonly region: HTMLDivElement;
  private readonly viewport: HTMLDivElement;
  private readonly dotsEl: HTMLDivElement;
  private readonly statusEl: HTMLDivElement;

  constructor() {
    super(sheet);

    this.region = document.createElement('div');
    this.region.className = 'carousel';
    this.region.setAttribute('part', 'carousel');
    this.region.setAttribute('role', 'region');
    this.region.setAttribute('aria-roledescription', 'carousel');

    this.viewport = document.createElement('div');
    this.viewport.className = 'viewport';
    this.viewport.setAttribute('part', 'viewport');

    const controls = document.createElement('div');
    controls.className = 'controls';
    controls.setAttribute('part', 'controls');

    const previous = this.makeStep('previous', 'Previous image');
    this.dotsEl = document.createElement('div');
    this.dotsEl.className = 'dots';
    this.dotsEl.setAttribute('part', 'dots');
    const next = this.makeStep('next', 'Next image');
    controls.append(previous, this.dotsEl, next);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'status';
    this.statusEl.setAttribute('aria-live', 'polite');

    this.region.append(this.viewport, controls, this.statusEl);
    this.root.append(this.region);

    this.addEventListener('keydown', (e) => this.onKeydown(e));
    this.addEventListener('mouseenter', () => {
      this.hovered = true;
      this.refreshTimer();
    });
    this.addEventListener('mouseleave', () => {
      this.hovered = false;
      this.refreshTimer();
    });
    this.addEventListener('focusin', () => {
      this.focused = true;
      this.refreshTimer();
    });
    this.addEventListener('focusout', () => {
      this.focused = false;
      this.refreshTimer();
    });
  }

  connectedCallback(): void {
    // Guarded the way split-view guards its own query: a host without matchMedia simply never
    // reports a preference, rather than throwing on connection.
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mql = window.matchMedia(REDUCED_MOTION);
      this.reducedMotion = this.mql.matches;
      this.mql.addEventListener('change', this.onMotionPreference);
    }
    this.playRequested = this.hasAttribute('autoplay');
    if (this.reducedMotion) this._index = 0;
    this.render();
    this.refreshTimer();
  }

  disconnectedCallback(): void {
    this.stopTimer();
    this.mql?.removeEventListener('change', this.onMotionPreference);
    this.mql = null;
  }

  attributeChangedCallback(name: string): void {
    if (name === 'autoplay') this.playRequested = this.hasAttribute('autoplay');
    this.applyState();
    this.refreshTimer();
  }

  /** The pictures, in the order they are shown (data in — a property, not an attribute). */
  get images(): CaosCarouselImage[] {
    return this._images;
  }
  set images(list: CaosCarouselImage[]) {
    this._images = usable(Array.isArray(list) ? list : []);
    // A shorter list must not leave the shown index past its end.
    this._index = Math.min(this._index, Math.max(0, this._images.length - 1));
    this.render();
    this.refreshTimer();
  }

  /** The picture shown now, counting from zero. */
  get index(): number {
    return this._index;
  }

  /** How many pictures the carousel is holding. */
  get count(): number {
    return this._images.length;
  }

  /** Whether the autoplay timer is running NOW — not whether it was asked for. */
  get playing(): boolean {
    return this.timer !== null;
  }

  /** Show a particular picture. Returns false when nothing changed. */
  goTo(index: number): boolean {
    if (!Number.isInteger(index) || index < 0 || index >= this._images.length) return false;
    if (index === this._index) return false;
    this._index = index;
    this.applyState();
    this.dispatchEvent(
      new CustomEvent('caos-carousel-change', {
        bubbles: true,
        composed: true,
        detail: { index: this._index, count: this._images.length },
      }),
    );
    return true;
  }

  /** Move forward one picture, wrapping from the last to the first. */
  next(): boolean {
    if (this._images.length === 0) return false;
    return this.goTo((this._index + 1) % this._images.length);
  }

  /** Move back one picture, wrapping from the first to the last. */
  previous(): boolean {
    if (this._images.length === 0) return false;
    return this.goTo((this._index - 1 + this._images.length) % this._images.length);
  }

  /** Start the autoplay timer. Refused outright while the reader asks for reduced motion. */
  play(): void {
    this.playRequested = true;
    this.refreshTimer();
  }

  /** Stop the autoplay timer, leaving the picture that is showing on screen. */
  pause(): void {
    this.playRequested = false;
    this.stopTimer();
    this.applyState();
  }

  private readonly onMotionPreference = (e: MediaQueryListEvent): void => {
    this.reducedMotion = e.matches;
    if (this.reducedMotion) {
      this.stopTimer();
      // Land on the first picture rather than stopping wherever the timer happened to leave it.
      this.goTo(0);
    }
    this.applyState();
    this.refreshTimer();
  };

  private makeStep(part: 'previous' | 'next', label: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `step step--${part}`;
    button.setAttribute('part', part);
    button.setAttribute('aria-label', label);
    const glyph = document.createElement('span');
    glyph.className = 'glyph';
    glyph.setAttribute('aria-hidden', 'true');
    glyph.append(makeIcon('chevron'));
    button.append(glyph);
    button.addEventListener('click', () => (part === 'next' ? this.next() : this.previous()));
    return button;
  }

  private render(): void {
    const count = this._images.length;
    const slides = this._images.map((image, i) => {
      const slide = document.createElement('div');
      slide.className = 'slide';
      slide.setAttribute('part', 'slide');
      slide.id = `${this.idBase}-slide-${i}`;
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${i + 1} of ${count}`);

      const frame = image.href
        ? Object.assign(document.createElement('a'), { href: image.href })
        : document.createElement('div');
      frame.className = 'link';

      const picture = document.createElement('img');
      picture.className = 'image';
      picture.setAttribute('part', 'image');
      picture.setAttribute('src', image.src);
      picture.setAttribute('alt', image.alt ?? '');
      frame.append(picture);

      // Absence is drawn as absence: no band at all rather than an empty one.
      if (image.heading || image.description) {
        const caption = document.createElement('div');
        caption.className = 'caption';
        caption.setAttribute('part', 'caption');
        if (image.heading) {
          const heading = document.createElement('div');
          heading.className = 'heading';
          heading.setAttribute('part', 'heading');
          heading.textContent = image.heading;
          caption.append(heading);
        }
        if (image.description) {
          const description = document.createElement('div');
          description.className = 'description';
          description.setAttribute('part', 'description');
          description.textContent = image.description;
          caption.append(description);
        }
        frame.append(caption);
      }

      slide.append(frame);
      return slide;
    });

    const dots = this._images.map((_image, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'dot';
      dot.setAttribute('part', 'dot');
      dot.setAttribute('aria-label', `Show image ${i + 1}`);
      dot.setAttribute('aria-controls', `${this.idBase}-slide-${i}`);
      dot.addEventListener('click', () => this.goTo(i));
      return dot;
    });

    this.viewport.replaceChildren(...slides);
    this.dotsEl.replaceChildren(...dots);
    this.applyState();
  }

  /** Reflect the shown index, the name and the announcement onto what is drawn. */
  private applyState(): void {
    this.region.setAttribute('aria-label', (this.getAttribute('label') ?? '').trim() || 'Carousel');

    const slides = [...this.viewport.querySelectorAll<HTMLElement>('.slide')];
    slides.forEach((slide, i) => {
      const current = i === this._index;
      slide.classList.toggle('is-current', current);
      // Hidden, not merely transparent: a slide nobody can see must not be tabbable either.
      slide.hidden = !current;
    });

    for (const [i, dot] of [...this.dotsEl.querySelectorAll<HTMLButtonElement>('.dot')].entries()) {
      if (i === this._index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }

    // Announce a move somebody made; stay quiet while autoplay is the one moving it.
    this.statusEl.setAttribute('aria-live', this.playing ? 'off' : 'polite');
    this.statusEl.textContent = this._images.length > 0 ? `Slide ${this._index + 1} of ${this._images.length}` : '';
  }

  private onKeydown(e: KeyboardEvent): void {
    const handled =
      e.key === 'ArrowRight'
        ? (): boolean => this.next()
        : e.key === 'ArrowLeft'
          ? (): boolean => this.previous()
          : e.key === 'Home'
            ? (): boolean => this.goTo(0)
            : e.key === 'End'
              ? (): boolean => this.goTo(this._images.length - 1)
              : null;
    if (!handled) return;
    e.preventDefault();
    handled();
  }

  private intervalMs(): number {
    if (!this.hasAttribute('interval')) return DEFAULT_INTERVAL;
    const raw = Number(this.getAttribute('interval'));
    // A value that is not a positive number leaves it still, rather than moving at an invented speed.
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  private refreshTimer(): void {
    this.stopTimer();
    this.startTimer();
    this.statusEl.setAttribute('aria-live', this.playing ? 'off' : 'polite');
  }

  private startTimer(): void {
    if (!this.isConnected) return;
    // The one thing no attribute and no caller can override.
    if (this.reducedMotion) return;
    if (!this.playRequested) return;
    // Nowhere to move to, so a timer would only burn wake-ups.
    if (this._images.length < 2) return;
    if (this.pausesOnHover() && (this.hovered || this.focused)) return;
    const ms = this.intervalMs();
    if (!(ms > 0)) return;
    this.timer = window.setInterval(() => this.next(), ms);
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Pausing is what happens unless a page asks for it not to.
   *
   * Declared as a NEGATIVE (`no-pause-on-hover`) because that is the only spelling that survives the
   * path a placement actually takes. The workspace writes a declared boolean the way HTML does —
   * present for true, absent for false (`applyProps`, host/src/surfaces/page-surface.ts) — and never
   * writes the string "false". A positive input that was on by default would therefore have had no
   * reachable off value at all. `noBorder` and `noIcon` are the same shape for the same reason.
   */
  private pausesOnHover(): boolean {
    return !this.hasAttribute('no-pause-on-hover');
  }
}

define('caos-carousel', CaosCarousel);

declare global {
  interface HTMLElementTagNameMap {
    'caos-carousel': CaosCarousel;
  }
}
